import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Play, Pause, Volume2, VolumeX, SkipBack, SkipForward,
  Mail, Headphones, Heart, Users, Mic, ExternalLink,
  Linkedin, Twitter, Facebook, Instagram, Youtube
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for social links
const socialIcons = {
  linkedin: Linkedin,
  twitter: Twitter,
  x: Twitter,
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  tiktok: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  ),
};

const LandingPage = () => {
  const [settings, setSettings] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch settings and social links from database
      const [settingsRes, linksRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/social-links?active_only=true`)
      ]);
      setSettings(settingsRes.data);
      setSocialLinks(linksRes.data);
      
      // Try to fetch episodes from RSS feed first, fallback to database
      try {
        const rssRes = await axios.get(`${API}/rss/episodes`);
        if (rssRes.data.episodes && rssRes.data.episodes.length > 0) {
          setEpisodes(rssRes.data.episodes);
        } else {
          // Fallback to database episodes
          const episodesRes = await axios.get(`${API}/episodes?published_only=true`);
          setEpisodes(episodesRes.data);
        }
      } catch (rssError) {
        // Fallback to database episodes
        const episodesRes = await axios.get(`${API}/episodes?published_only=true`);
        setEpisodes(episodesRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Successfully subscribed to the newsletter!");
      setEmail("");
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setIsSubscribing(false);
    }
  };

  const playEpisode = (episode) => {
    if (currentEpisode?.id === episode.id) {
      togglePlayPause();
    } else {
      setCurrentEpisode(episode);
      setIsPlaying(true);
      setProgress(0);
    }
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const prog = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(prog || 0);
    }
  };

  const handleProgressChange = (e) => {
    const newProgress = parseFloat(e.target.value);
    setProgress(newProgress);
    if (audioRef.current && audioRef.current.duration) {
      audioRef.current.currentTime = (newProgress / 100) * audioRef.current.duration;
    }
  };

  const getSocialIcon = (platform) => {
    const Icon = socialIcons[platform.toLowerCase()];
    if (typeof Icon === 'function') return <Icon />;
    if (Icon) return <Icon className="w-5 h-5" />;
    return <ExternalLink className="w-5 h-5" />;
  };

  const defaultSettings = {
    podcast_name: "Everyday Echoes",
    tagline: "Reflecting Life, One Story at a Time",
    description: "Discover the extraordinary in the ordinary with Everyday Echoes, a podcast that brings heartfelt and inspiring stories to life.",
    host_name: "Nana Kwame Okyere-Tawiah",
    host_bio: "Nana Kwame Okyere-Tawiah is the creator and host of Everyday Echoes. With a passion for storytelling and human connection, he brings heartfelt and inspiring stories to life through meaningful conversations that resonate long after the episode ends.",
    host_image: "https://customer-assets.emergentagent.com/job_everyday-echoes/artifacts/794o6oyx_IMG_2936.jpeg",
    logo_url: "https://customer-assets.emergentagent.com/job_everyday-echoes/artifacts/d0tyh45u_Everyday%20Echoes%20Podcast%20Logo%20Sound%20and%20Conversation.png",
    podcast_url: "https://everydayechoes.podbean.com/"
  };

  const s = settings || defaultSettings;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="landing-page">
      {/* Hidden audio element */}
      {currentEpisode?.audio_url && (
        <audio
          ref={audioRef}
          src={currentEpisode.audio_url}
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          autoPlay
        />
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 glass" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <a href="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src={s.logo_url} 
                alt={s.podcast_name} 
                className="h-14 sm:h-16 w-auto"
              />
            </a>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#about" className="text-[#4A5555] hover:text-[#2E8B8B] transition-colors font-medium">About</a>
              <a href="#episodes" className="text-[#4A5555] hover:text-[#2E8B8B] transition-colors font-medium">Episodes</a>
              <a href="#subscribe" className="text-[#4A5555] hover:text-[#2E8B8B] transition-colors font-medium">Subscribe</a>
            </nav>
            <a 
              href={s.podcast_url} 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="listen-now-btn"
            >
              <Button className="bg-[#E67E22] hover:bg-[#D9731E] text-white rounded-full px-6">
                Listen Now
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 sm:pt-40 pb-20 sm:pb-32 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-in-up">
              <p className="text-[#E67E22] font-semibold tracking-wider uppercase text-sm mb-4">
                A Podcast by {s.host_name}
              </p>
              <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#1A2323] tracking-tight leading-[1.1] mb-6">
                {s.podcast_name}
              </h1>
              <p className="text-xl sm:text-2xl text-[#2E8B8B] font-serif italic mb-6">
                {s.tagline}
              </p>
              <p className="text-[#4A5555] text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
                {s.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={s.podcast_url} target="_blank" rel="noopener noreferrer">
                  <Button 
                    className="bg-[#2E8B8B] hover:bg-[#1F6666] text-white rounded-full px-8 py-6 text-lg font-medium"
                    data-testid="subscribe-now-btn"
                  >
                    <Headphones className="w-5 h-5 mr-2" />
                    Subscribe Now
                  </Button>
                </a>
                <a href="#episodes">
                  <Button 
                    variant="outline" 
                    className="border-2 border-[#2E8B8B] text-[#2E8B8B] hover:bg-[#2E8B8B]/10 rounded-full px-8 py-6 text-lg font-medium"
                    data-testid="browse-episodes-btn"
                  >
                    Browse Episodes
                  </Button>
                </a>
              </div>
            </div>
            <div className="animate-fade-in-up stagger-2 flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#2E8B8B]/20 to-[#E67E22]/20 rounded-3xl blur-2xl"></div>
                <img 
                  src={s.logo_url}
                  alt={s.podcast_name}
                  className="relative w-72 sm:w-96 lg:w-[450px] h-auto rounded-2xl shadow-2xl"
                  data-testid="hero-logo"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#F5F2EB]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Mic, label: "Episodes", value: episodes.length || "Coming Soon" },
              { icon: Heart, label: "Stories Shared", value: "Countless" },
              { icon: Users, label: "Community", value: "Growing" },
              { icon: Headphones, label: "Hours of Content", value: "Inspiring" },
            ].map((stat, i) => (
              <div key={i} className="text-center animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <stat.icon className="w-8 h-8 text-[#E67E22] mx-auto mb-3" />
                <p className="text-2xl sm:text-3xl font-serif font-semibold text-[#1A2323]">{stat.value}</p>
                <p className="text-[#4A5555] text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Host Section */}
      <section id="about" className="py-20 sm:py-32 px-4 sm:px-6 lg:px-8" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1 animate-fade-in-up">
              <p className="text-[#E67E22] font-semibold tracking-wider uppercase text-sm mb-4">
                Meet Your Host
              </p>
              <h2 className="font-serif font-medium text-3xl sm:text-4xl text-[#1A2323] tracking-tight mb-6">
                {s.host_name}
              </h2>
              <p className="text-[#4A5555] text-base sm:text-lg leading-relaxed mb-6">
                {s.host_bio || `${s.host_name} is the creator and host of Everyday Echoes. With a passion for storytelling and human connection, he brings heartfelt and inspiring stories to life through meaningful conversations that resonate long after the episode ends.`}
              </p>
              <p className="text-[#4A5555] text-base sm:text-lg leading-relaxed mb-8">
                Each episode features real stories from everyday people, capturing the raw emotions and shared experiences that tie us all together. From tales of overcoming adversity to quiet moments of joy and introspection, Everyday Echoes celebrates the incredible breadth of the human experience.
              </p>
              {socialLinks.length > 0 && (
                <div className="flex gap-3">
                  {socialLinks.map((link) => (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-icon"
                      data-testid={`social-link-${link.platform}`}
                    >
                      {getSocialIcon(link.platform)}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="order-1 lg:order-2 animate-fade-in-up stagger-2 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#E67E22]/20 to-[#2E8B8B]/20 rounded-3xl blur-2xl"></div>
                <img 
                  src={s.host_image}
                  alt={s.host_name}
                  className="relative w-72 sm:w-96 h-auto rounded-2xl shadow-2xl object-cover"
                  data-testid="host-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Episodes Section */}
      <section id="episodes" className="py-20 sm:py-32 px-4 sm:px-6 lg:px-8 bg-[#F5F2EB]" data-testid="episodes-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in-up">
            <p className="text-[#E67E22] font-semibold tracking-wider uppercase text-sm mb-4">
              Latest Episodes
            </p>
            <h2 className="font-serif font-medium text-3xl sm:text-4xl text-[#1A2323] tracking-tight mb-4">
              Start Listening
            </h2>
            <p className="text-[#4A5555] text-base sm:text-lg max-w-2xl mx-auto">
              Dive into our collection of heartfelt stories and meaningful conversations.
            </p>
          </div>

          {episodes.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {episodes.map((episode, i) => (
                <div 
                  key={episode.id}
                  className="episode-card bg-white rounded-xl overflow-hidden border border-[#E5E0D6] animate-fade-in-up"
                  style={{ animationDelay: `${i * 0.1}s` }}
                  data-testid={`episode-card-${episode.id}`}
                >
                  <div className="relative aspect-square">
                    <img 
                      src={episode.cover_image || "https://images.unsplash.com/photo-1478737270239-2f02b77ac6d5?auto=format&fit=crop&w=800&q=80"}
                      alt={episode.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => playEpisode(episode)}
                        className="w-16 h-16 rounded-full bg-[#E67E22] text-white flex items-center justify-center hover:scale-110 transition-transform"
                        data-testid={`play-episode-${episode.id}`}
                      >
                        {currentEpisode?.id === episode.id && isPlaying ? (
                          <Pause className="w-7 h-7" />
                        ) : (
                          <Play className="w-7 h-7 ml-1" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="p-5">
                    {episode.episode_number && (
                      <p className="text-[#2E8B8B] text-xs font-semibold tracking-wider uppercase mb-2">
                        Episode {episode.episode_number}
                      </p>
                    )}
                    <h3 className="font-serif font-medium text-lg text-[#1A2323] mb-2 line-clamp-2">
                      {episode.title}
                    </h3>
                    <p className="text-[#4A5555] text-sm line-clamp-2 mb-3">
                      {episode.description}
                    </p>
                    {episode.duration && (
                      <p className="text-[#4A5555] text-xs">{episode.duration}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-[#E5E0D6]">
              <Mic className="w-16 h-16 text-[#E67E22] mx-auto mb-4 opacity-50" />
              <h3 className="font-serif text-2xl text-[#1A2323] mb-2">Coming Soon</h3>
              <p className="text-[#4A5555] max-w-md mx-auto">
                We're preparing amazing content for you. Subscribe to be notified when new episodes drop!
              </p>
            </div>
          )}

          <div className="text-center mt-12">
            <a href={s.podcast_url} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-[#2E8B8B] hover:bg-[#1F6666] text-white rounded-full px-8 py-6 text-lg font-medium"
                data-testid="view-all-episodes-btn"
              >
                View All Episodes
                <ExternalLink className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Subscribe/Listen Section */}
      <section id="subscribe" className="py-20 sm:py-32 px-4 sm:px-6 lg:px-8" data-testid="subscribe-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in-up">
            <p className="text-[#E67E22] font-semibold tracking-wider uppercase text-sm mb-4">
              Never Miss an Episode
            </p>
            <h2 className="font-serif font-medium text-3xl sm:text-4xl text-[#1A2323] tracking-tight mb-4">
              Listen on Your Favorite Platform
            </h2>
            <p className="text-[#4A5555] text-base sm:text-lg max-w-2xl mx-auto">
              Subscribe now and join a community moved by life's most powerful stories.
            </p>
          </div>

          {/* Platform Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-16 max-w-4xl mx-auto animate-fade-in-up stagger-2">
            {[
              { 
                name: "Podbean", 
                url: s.podcast_url, 
                color: "#1A5E1F",
                bgColor: "bg-gradient-to-br from-[#4CAF50] to-[#2E7D32]",
                icon: (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                )
              },
              { 
                name: "Spotify", 
                url: "#", 
                color: "#1DB954",
                bgColor: "bg-gradient-to-br from-[#1DB954] to-[#169c46]",
                icon: (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                )
              },
              { 
                name: "Apple Podcasts", 
                url: "#", 
                color: "#FA57C1",
                bgColor: "bg-gradient-to-br from-[#FA57C1] to-[#8B5CF6]",
                icon: (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                    <path d="M5.34 0A5.328 5.328 0 000 5.34v13.32A5.328 5.328 0 005.34 24h13.32A5.328 5.328 0 0024 18.66V5.34A5.328 5.328 0 0018.66 0H5.34zm6.525 2.568c2.336 0 4.448.902 6.056 2.587 1.224 1.272 1.912 2.619 2.264 4.392.12.59-.26 1.166-.852 1.282-.59.121-1.166-.259-1.282-.852-.256-1.29-.756-2.281-1.64-3.201-1.267-1.324-2.924-2.053-4.667-2.053-1.743 0-3.398.73-4.664 2.053-.885.918-1.384 1.91-1.64 3.201-.117.593-.691.973-1.283.852-.592-.116-.972-.692-.853-1.282.353-1.773 1.04-3.12 2.265-4.392 1.608-1.685 3.719-2.587 6.055-2.587h.24zm-.12 3.96a4.362 4.362 0 013.108 1.292 4.36 4.36 0 011.292 3.108c0 1.044-.304 2.01-.912 2.892-.388.563-.474 1.089-.26 1.578l.927 2.172c.317.739.239 1.542-.209 2.147-.448.606-1.154.964-1.888.964h-4.073c-.734 0-1.44-.358-1.888-.963-.449-.606-.527-1.408-.209-2.148l.927-2.172c.214-.489.127-1.015-.26-1.578a4.362 4.362 0 01-.912-2.892 4.36 4.36 0 011.292-3.108 4.362 4.362 0 013.108-1.292h-.043zm-.12 2.4a1.92 1.92 0 00-1.92 1.92c0 .576.24 1.08.624 1.44.516.48.696 1.2.48 1.872l-.927 2.172a.48.48 0 00.048.492.48.48 0 00.432.216h4.073a.48.48 0 00.432-.216.48.48 0 00.048-.492l-.927-2.172c-.216-.672-.036-1.392.48-1.872a1.92 1.92 0 00.624-1.44 1.92 1.92 0 00-1.92-1.92h-1.547z"/>
                  </svg>
                )
              },
              { 
                name: "YouTube", 
                url: "#", 
                color: "#FF0000",
                bgColor: "bg-gradient-to-br from-[#FF0000] to-[#CC0000]",
                icon: (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                )
              },
            ].map((platform, index) => (
              <a 
                key={platform.name}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`group relative overflow-hidden rounded-2xl ${platform.bgColor} p-6 transition-all duration-300 hover:scale-105 hover:shadow-xl`}
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`platform-${platform.name.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex flex-col items-center text-white">
                  <div className="mb-3 opacity-90 group-hover:opacity-100 transition-opacity">
                    {platform.icon}
                  </div>
                  <span className="font-semibold text-sm sm:text-base">{platform.name}</span>
                  <span className="text-xs opacity-75 mt-1 group-hover:opacity-100 transition-opacity">
                    Listen Now →
                  </span>
                </div>
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            ))}
          </div>

          {/* Newsletter */}
          <div className="max-w-xl mx-auto bg-[#1A2323] rounded-2xl p-8 sm:p-10 text-center animate-fade-in-up stagger-3">
            <Mail className="w-12 h-12 text-[#E67E22] mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-white mb-3">
              Join Our Newsletter
            </h3>
            <p className="text-gray-400 mb-6">
              Get updates on new episodes, behind-the-scenes content, and exclusive stories delivered to your inbox.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
              <Input 
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400 rounded-full px-6"
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit"
                disabled={isSubscribing}
                className="bg-[#E67E22] hover:bg-[#D9731E] text-white rounded-full px-8"
                data-testid="newsletter-submit-btn"
              >
                {isSubscribing ? "Subscribing..." : "Subscribe"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-4 sm:px-6 lg:px-8 bg-[#1A2323]" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            {/* Logo and Description */}
            <div className="md:col-span-1">
              <a href="/" className="flex items-center gap-3 mb-4">
                <img 
                  src={s.logo_url}
                  alt={s.podcast_name}
                  className="h-12 w-auto"
                />
              </a>
              <p className="text-gray-400 text-sm leading-relaxed">
                {s.tagline}
              </p>
            </div>
            
            {/* Quick Links */}
            <div className="md:col-span-1">
              <h4 className="font-serif text-white font-medium mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#about" className="text-gray-400 hover:text-[#E67E22] transition-colors text-sm">About</a></li>
                <li><a href="#episodes" className="text-gray-400 hover:text-[#E67E22] transition-colors text-sm">Episodes</a></li>
                <li><a href="#subscribe" className="text-gray-400 hover:text-[#E67E22] transition-colors text-sm">Subscribe</a></li>
                <li><a href={s.podcast_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#E67E22] transition-colors text-sm">Podbean</a></li>
              </ul>
            </div>
            
            {/* Social Links */}
            <div className="md:col-span-1">
              <h4 className="font-serif text-white font-medium mb-4">Follow Along</h4>
              {socialLinks.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {socialLinks.map((link) => (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-[#E67E22] transition-colors"
                      data-testid={`footer-social-${link.platform}`}
                      title={link.platform}
                    >
                      {getSocialIcon(link.platform)}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} {s.podcast_name}. All rights reserved.
            </p>
            <p className="text-gray-500 text-xs">
              Hosted by {s.host_name}
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky Audio Player */}
      {currentEpisode && (
        <div className="fixed bottom-0 left-0 right-0 z-50 audio-player-bg border-t border-white/10 shadow-2xl" data-testid="audio-player">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-4">
              <img 
                src={currentEpisode.cover_image || "https://images.unsplash.com/photo-1478737270239-2f02b77ac6d5?auto=format&fit=crop&w=800&q=80"}
                alt={currentEpisode.title}
                className="w-12 h-12 rounded-lg object-cover"
              />
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{currentEpisode.title}</p>
                <p className="text-gray-400 text-xs truncate">{s.podcast_name}</p>
              </div>
              <div className="flex items-center gap-3">
                <button className="text-white/60 hover:text-white transition-colors">
                  <SkipBack className="w-5 h-5" />
                </button>
                <button 
                  onClick={togglePlayPause}
                  className="w-10 h-10 rounded-full bg-[#E67E22] text-white flex items-center justify-center hover:scale-105 transition-transform"
                  data-testid="audio-play-pause-btn"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </button>
                <button className="text-white/60 hover:text-white transition-colors">
                  <SkipForward className="w-5 h-5" />
                </button>
              </div>
              <div className="hidden sm:flex items-center gap-3 flex-1 max-w-xs">
                <input 
                  type="range"
                  min="0"
                  max="100"
                  value={progress}
                  onChange={handleProgressChange}
                  className="audio-progress flex-1"
                  data-testid="audio-progress-bar"
                />
              </div>
              <button 
                onClick={toggleMute}
                className="text-white/60 hover:text-white transition-colors hidden sm:block"
                data-testid="audio-mute-btn"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
